import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type Monitor } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface FormState {
  slug: string;
  name: string;
  url: string;
  method: 'GET' | 'HEAD' | 'POST';
  expected_status: number;
  expected_keyword: string;
  timeout_ms: number;
  interval_seconds: number;
  follow_redirects: boolean;
  enabled: boolean;
  public: boolean;
  notify_email: string;
  notify_slack: string;
  notify_webhook: string;
  failure_threshold: number;
  recovery_threshold: number;
}

const EMPTY: FormState = {
  slug: '',
  name: '',
  url: '',
  method: 'GET',
  expected_status: 200,
  expected_keyword: '',
  timeout_ms: 10_000,
  interval_seconds: 300,
  follow_redirects: true,
  enabled: true,
  public: false,
  notify_email: '',
  notify_slack: '',
  notify_webhook: '',
  failure_threshold: 2,
  recovery_threshold: 1,
};

export function MonitorForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const [f, setF] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!id) return;
    api
      .getMonitor(id)
      .then(({ monitor }) => {
        setF(fromMonitor(monitor));
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'load failed'));
  }, [id]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const payload = toPayload(f);
      if (isEdit && id) {
        await api.updateMonitor(id, payload);
        nav(`/admin/monitors/${id}`);
      } else {
        const { monitor } = await api.createMonitor(payload);
        nav(`/admin/monitors/${monitor.id}`);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-6 max-w-2xl">
      <h1 className="text-2xl font-semibold tracking-tight">
        {isEdit ? 'Edit monitor' : 'New monitor'}
      </h1>

      {error && (
        <Card className="p-4 border-destructive/50 text-destructive text-sm">{error}</Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Basics</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Field label="Name">
            <Input
              value={f.name}
              onChange={(e) => setF({ ...f, name: e.target.value })}
              required
              placeholder="API production"
            />
          </Field>
          <Field label="Slug" hint="URL-safe identifier for the public status page.">
            <Input
              value={f.slug}
              onChange={(e) =>
                setF({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })
              }
              required
              placeholder="api-prod"
            />
          </Field>
          <Field label="URL">
            <Input
              type="url"
              value={f.url}
              onChange={(e) => setF({ ...f, url: e.target.value })}
              required
              placeholder="https://example.com/healthz"
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Method">
              <select
                className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
                value={f.method}
                onChange={(e) => setF({ ...f, method: e.target.value as FormState['method'] })}
              >
                <option value="GET">GET</option>
                <option value="HEAD">HEAD</option>
                <option value="POST">POST</option>
              </select>
            </Field>
            <Field label="Interval">
              <select
                className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
                value={f.interval_seconds}
                onChange={(e) =>
                  setF({ ...f, interval_seconds: parseInt(e.target.value, 10) })
                }
              >
                <option value={60}>1 minute</option>
                <option value={300}>5 minutes</option>
                <option value={900}>15 minutes</option>
                <option value={1800}>30 minutes</option>
                <option value={3600}>1 hour</option>
              </select>
            </Field>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Matching</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Expected status">
              <Input
                type="number"
                value={f.expected_status}
                onChange={(e) =>
                  setF({ ...f, expected_status: parseInt(e.target.value, 10) })
                }
                min={100}
                max={599}
              />
            </Field>
            <Field label="Timeout (ms)">
              <Input
                type="number"
                value={f.timeout_ms}
                onChange={(e) => setF({ ...f, timeout_ms: parseInt(e.target.value, 10) })}
                min={1000}
                max={30_000}
              />
            </Field>
          </div>
          <Field
            label="Expected keyword"
            hint="Optional. If set, the response body must contain this substring."
          >
            <Input
              value={f.expected_keyword}
              onChange={(e) => setF({ ...f, expected_keyword: e.target.value })}
              placeholder="OK"
            />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Failure threshold">
              <Input
                type="number"
                value={f.failure_threshold}
                onChange={(e) =>
                  setF({ ...f, failure_threshold: parseInt(e.target.value, 10) })
                }
                min={1}
                max={10}
              />
            </Field>
            <Field label="Recovery threshold">
              <Input
                type="number"
                value={f.recovery_threshold}
                onChange={(e) =>
                  setF({ ...f, recovery_threshold: parseInt(e.target.value, 10) })
                }
                min={1}
                max={10}
              />
            </Field>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Notifications</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Field
            label="Email"
            hint="Comma-separated. Leave empty to use the default admin list."
          >
            <Input
              value={f.notify_email}
              onChange={(e) => setF({ ...f, notify_email: e.target.value })}
              placeholder="oncall@example.com,alerts@example.com"
            />
          </Field>
          <Field
            label="Slack webhook"
            hint="Overrides the default. Get one at slack.com/services/new/incoming-webhook"
          >
            <Input
              type="url"
              value={f.notify_slack}
              onChange={(e) => setF({ ...f, notify_slack: e.target.value })}
              placeholder="https://hooks.slack.com/services/..."
            />
          </Field>
          <Field label="Generic webhook">
            <Input
              type="url"
              value={f.notify_webhook}
              onChange={(e) => setF({ ...f, notify_webhook: e.target.value })}
              placeholder="https://your-service.example.com/uptime-hook"
            />
          </Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Visibility</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Toggle
            checked={f.enabled}
            onChange={(v) => setF({ ...f, enabled: v })}
            label="Enabled"
            hint="Paused monitors don't run checks and don't page."
          />
          <Toggle
            checked={f.public}
            onChange={(v) => setF({ ...f, public: v })}
            label="Show on public status page"
          />
          <Toggle
            checked={f.follow_redirects}
            onChange={(v) => setF({ ...f, follow_redirects: v })}
            label="Follow redirects"
          />
        </CardContent>
      </Card>

      <div className="flex gap-2">
        <Button type="submit" disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create monitor'}
        </Button>
        <Button type="button" variant="ghost" onClick={() => nav(-1)}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function Toggle({
  checked,
  onChange,
  label,
  hint,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  hint?: string;
}) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-4 w-4 rounded border-input"
      />
      <div>
        <div className="text-sm font-medium">{label}</div>
        {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
      </div>
    </label>
  );
}

function fromMonitor(m: Monitor): FormState {
  return {
    slug: m.slug,
    name: m.name,
    url: m.url,
    method: m.method,
    expected_status: m.expected_status,
    expected_keyword: m.expected_keyword ?? '',
    timeout_ms: m.timeout_ms,
    interval_seconds: m.interval_seconds,
    follow_redirects: m.follow_redirects === 1,
    enabled: m.enabled === 1,
    public: m.public === 1,
    notify_email: m.notify_email ?? '',
    notify_slack: m.notify_slack ?? '',
    notify_webhook: m.notify_webhook ?? '',
    failure_threshold: m.failure_threshold,
    recovery_threshold: m.recovery_threshold,
  };
}

function toPayload(f: FormState) {
  return {
    slug: f.slug,
    name: f.name,
    url: f.url,
    method: f.method,
    expected_status: f.expected_status,
    expected_keyword: f.expected_keyword || null,
    timeout_ms: f.timeout_ms,
    interval_seconds: f.interval_seconds,
    follow_redirects: f.follow_redirects,
    enabled: f.enabled,
    public: f.public,
    notify_email: f.notify_email || null,
    notify_slack: f.notify_slack || null,
    notify_webhook: f.notify_webhook || null,
    failure_threshold: f.failure_threshold,
    recovery_threshold: f.recovery_threshold,
  };
}
